import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrganization } from '@clerk/clerk-react'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import './V2Onboarding.css'

const STEPS = [
  { label: 'Company' },
  { label: 'Provider' },
  { label: 'Review' },
]

const INDUSTRIES = [
  'Construction & Building Materials',
  'Consumer Electronics',
  'Furniture & Home Goods',
  'Automotive Parts',
  'Textiles & Apparel',
  'Food & Beverage',
  'Industrial Equipment',
  'Other',
]

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'MXN']
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'Europe/London',
  'Asia/Shanghai',
]

const PROVIDER_TYPES = [
  { value: 'ocean_freight', label: 'International Ocean Freight' },
  { value: 'domestic_trucking', label: 'Domestic Trucking' },
]

// Which truckload modes a carrier handles. Decides whether an FTL or an LTL
// quote request reaches them at all, so trucking carriers must pick one.
const TRUCKING_MODES = [
  { value: 'FTL', label: 'FTL only (full truckload)' },
  { value: 'LTL', label: 'LTL only (less-than-truckload)' },
  { value: 'both', label: 'Both FTL and LTL' },
]

const handlesTrucking = (serviceType) =>
  serviceType === 'domestic_trucking' || serviceType === 'both'

export default function V2OnboardingWizard() {
  const navigate = useNavigate()
  const api = useV2Api()
  const { organization } = useOrganization()
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [errorFallback, setErrorFallback] = useState(null)
  const [touched, setTouched] = useState({})
  const [form, setForm] = useState({
    companyName: organization?.name || '',
    industry: '',
    currency: 'USD',
    timezone: 'America/New_York',
    providerName: '',
    providerEmail: '',
    providerType: '',
    providerTruckingMode: '',
  })

  // Async fallback: if Clerk organization wasn't available on first render,
  // fill in the company name once it loads (MTEU-441).
  useEffect(() => {
    if (organization?.name) {
      setForm((prev) => ({ ...prev, companyName: prev.companyName || organization.name }))
    }
  }, [organization?.name])

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const markTouched = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const canContinue = () => {
    if (step === 0) return form.companyName.trim() && form.industry
    if (step === 1) return true // provider is optional
    return true
  }

  // Validation errors for step 0
  const step0Errors = {
    companyName: touched.companyName && !form.companyName.trim() ? 'Company name is required' : null,
    industry: touched.industry && !form.industry ? 'Industry is required' : null,
  }

  // Validation for provider step (only if user started filling it in)
  const providerStarted = form.providerName || form.providerEmail
  const step1Errors = {
    providerName: providerStarted && touched.providerName && !form.providerName.trim() ? 'Provider name is required' : null,
    providerEmail: providerStarted && touched.providerEmail && !form.providerEmail.trim()
      ? 'Provider email is required'
      : providerStarted && touched.providerEmail && !isValidEmail(form.providerEmail)
        ? 'Enter a valid email address'
        : null,
    providerType: providerStarted && touched.providerType && !form.providerType ? 'Provider type is required' : null,
    // A trucking carrier without a truckload mode would be created and then
    // never receive an RFQ, so the backend rejects it — ask here instead.
    providerTruckingMode:
      providerStarted && handlesTrucking(form.providerType) && touched.providerTruckingMode && !form.providerTruckingMode
        ? 'Truckload mode is required for trucking carriers'
        : null,
  }

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1)
    }
  }

  const handleComplete = async () => {
    setError(null); setErrorFallback(null)
    const hasProvider =
      form.providerName &&
      form.providerEmail &&
      form.providerType &&
      (!handlesTrucking(form.providerType) || form.providerTruckingMode)
    if (hasProvider) {
      setSaving(true)
      try {
        // Map service type to the legacy backend type enum
        const backendType = form.providerType === 'domestic_trucking' ? 'CARRIER' : 'FORWARDER'
        await api.request('/api/v2/providers', {
          method: 'POST',
          body: JSON.stringify({
            name: form.providerName,
            type: backendType,
            contactEmail: form.providerEmail,
            serviceType: form.providerType,
            truckingMode: handlesTrucking(form.providerType) ? form.providerTruckingMode : null
          })
        })
      } catch (err) {
        // Pass the Error so friendlyErrorMessage scrubs raw bodies; the fallback
        // text preserves the original "Couldn't save provider" guidance.
        setError(err)
        setErrorFallback("Couldn't save provider. You can add it manually from the Providers page.")
        setSaving(false)
        return
      }
      setSaving(false)
    }
    navigate('/dashboard')
  }

  const handleSkip = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    } else {
      navigate('/dashboard')
    }
  }

  const renderStepDot = (index) => {
    let className = 'v2-onboarding-step-dot'
    if (index < step) className += ' v2-onboarding-step-dot--done'
    else if (index === step) className += ' v2-onboarding-step-dot--active'
    return (
      <span className={className}>
        {index < step ? '\u2713' : index + 1}
      </span>
    )
  }

  return (
    <div className="v2-onboarding">
      <div className="v2-onboarding-card">
        <div className="v2-onboarding-header">
          <h1 className="v2-onboarding-title">Set up your workspace</h1>
          <p className="v2-onboarding-subtitle">
            Just a few steps to get you started
          </p>
        </div>

        {/* Progress Bar */}
        <div className="v2-onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s.label} className="v2-onboarding-step-indicator">
              {renderStepDot(i)}
              {i < STEPS.length - 1 && (
                <div className={`v2-onboarding-step-line${i < step ? ' v2-onboarding-step-line--done' : ''}`} />
              )}
            </div>
          ))}
        </div>

        <div className="v2-onboarding-step-labels">
          {STEPS.map((s, i) => (
            <span
              key={s.label}
              className={`v2-onboarding-step-label${i === step ? ' v2-onboarding-step-label--active' : ''}`}
            >
              {s.label}
            </span>
          ))}
        </div>

        {/* Step 1: Company Details */}
        {step === 0 && (
          <div className="v2-onboarding-step">
            <h3 className="v2-onboarding-step-title">Company Details</h3>

            <div className="v2-form-group">
              <label className="v2-form-label" htmlFor="ob-company">
                Company name <span className="v2-form-required">*</span>
              </label>
              <input
                id="ob-company"
                className={`v2-form-input${step0Errors.companyName ? ' v2-form-input--error' : ''}`}
                type="text"
                placeholder="Enter your company name"
                value={form.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                onBlur={() => markTouched('companyName')}
              />
              {step0Errors.companyName && (
                <span className="v2-form-error">{step0Errors.companyName}</span>
              )}
            </div>

            <div className="v2-form-group">
              <label className="v2-form-label" htmlFor="ob-industry">
                Industry <span className="v2-form-required">*</span>
              </label>
              <select
                id="ob-industry"
                className={`v2-form-input${!form.industry ? ' v2-form-input--placeholder' : ''}${step0Errors.industry ? ' v2-form-input--error' : ''}`}
                value={form.industry}
                onChange={(e) => updateField('industry', e.target.value)}
                onBlur={() => markTouched('industry')}
              >
                <option value="" disabled>Select industry</option>
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
              {step0Errors.industry && (
                <span className="v2-form-error">{step0Errors.industry}</span>
              )}
            </div>

            <div className="v2-onboarding-row">
              <div className="v2-form-group">
                <label className="v2-form-label" htmlFor="ob-currency">Currency</label>
                <select
                  id="ob-currency"
                  className="v2-form-input"
                  value={form.currency}
                  onChange={(e) => updateField('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="v2-form-group">
                <label className="v2-form-label" htmlFor="ob-tz">Timezone</label>
                <select
                  id="ob-tz"
                  className="v2-form-input"
                  value={form.timezone}
                  onChange={(e) => updateField('timezone', e.target.value)}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Add First Provider */}
        {step === 1 && (
          <div className="v2-onboarding-step">
            <h3 className="v2-onboarding-step-title">Add your first provider (optional)</h3>

            <div className="v2-form-group">
              <label className="v2-form-label" htmlFor="ob-prov-name">Provider name</label>
              <input
                id="ob-prov-name"
                className={`v2-form-input${step1Errors.providerName ? ' v2-form-input--error' : ''}`}
                type="text"
                placeholder="e.g. DHL Global Forwarding"
                value={form.providerName}
                onChange={(e) => updateField('providerName', e.target.value)}
                onBlur={() => markTouched('providerName')}
              />
              {step1Errors.providerName && (
                <span className="v2-form-error">{step1Errors.providerName}</span>
              )}
            </div>

            <div className="v2-form-group">
              <label className="v2-form-label" htmlFor="ob-prov-email">Provider email</label>
              <input
                id="ob-prov-email"
                className={`v2-form-input${step1Errors.providerEmail ? ' v2-form-input--error' : ''}`}
                type="email"
                placeholder="quotes@provider.com"
                value={form.providerEmail}
                onChange={(e) => updateField('providerEmail', e.target.value)}
                onBlur={() => markTouched('providerEmail')}
              />
              {step1Errors.providerEmail && (
                <span className="v2-form-error">{step1Errors.providerEmail}</span>
              )}
            </div>

            <div className="v2-form-group">
              <label className="v2-form-label" htmlFor="ob-prov-type">Provider type</label>
              <select
                id="ob-prov-type"
                className={`v2-form-input${!form.providerType ? ' v2-form-input--placeholder' : ''}${step1Errors.providerType ? ' v2-form-input--error' : ''}`}
                value={form.providerType}
                onChange={(e) => updateField('providerType', e.target.value)}
                onBlur={() => markTouched('providerType')}
              >
                <option value="" disabled>Select provider type</option>
                {PROVIDER_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {step1Errors.providerType && (
                <span className="v2-form-error">{step1Errors.providerType}</span>
              )}
            </div>

            {handlesTrucking(form.providerType) && (
              <div className="v2-form-group">
                <label className="v2-form-label" htmlFor="ob-prov-trucking-mode">Truckload mode</label>
                <select
                  id="ob-prov-trucking-mode"
                  className={`v2-form-input${!form.providerTruckingMode ? ' v2-form-input--placeholder' : ''}${step1Errors.providerTruckingMode ? ' v2-form-input--error' : ''}`}
                  value={form.providerTruckingMode}
                  onChange={(e) => updateField('providerTruckingMode', e.target.value)}
                  onBlur={() => markTouched('providerTruckingMode')}
                >
                  <option value="" disabled>Select truckload mode</option>
                  {TRUCKING_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <span className="v2-form-label" style={{ fontWeight: 400, opacity: 0.7 }}>
                  Only quote requests for the modes selected here reach this carrier.
                </span>
                {step1Errors.providerTruckingMode && (
                  <span className="v2-form-error">{step1Errors.providerTruckingMode}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review & Summary */}
        {step === 2 && (
          <div className="v2-onboarding-step">
            <h3 className="v2-onboarding-step-title">You&apos;re all set!</h3>

            <div className="v2-onboarding-summary">
              <div className="v2-onboarding-summary-item">
                <div className="v2-onboarding-summary-icon v2-onboarding-summary-icon--text">Co</div>
                <div className="v2-onboarding-summary-info">
                  <span className="v2-onboarding-summary-label">Company</span>
                  <span className="v2-onboarding-summary-value">
                    {form.companyName || 'Not set'}
                  </span>
                </div>
              </div>

              <div className="v2-onboarding-summary-item">
                <div className="v2-onboarding-summary-icon v2-onboarding-summary-icon--text">Ind</div>
                <div className="v2-onboarding-summary-info">
                  <span className="v2-onboarding-summary-label">Industry</span>
                  <span className="v2-onboarding-summary-value">
                    {form.industry || 'Not set'}
                  </span>
                </div>
              </div>

              <div className="v2-onboarding-summary-item">
                <div className="v2-onboarding-summary-icon v2-onboarding-summary-icon--text">Cfg</div>
                <div className="v2-onboarding-summary-info">
                  <span className="v2-onboarding-summary-label">Currency / Timezone</span>
                  <span className="v2-onboarding-summary-value">
                    {form.currency} / {form.timezone}
                  </span>
                </div>
              </div>

              {form.providerName && (
                <div className="v2-onboarding-summary-item">
                  <div className="v2-onboarding-summary-icon v2-onboarding-summary-icon--text">Pr</div>
                  <div className="v2-onboarding-summary-info">
                    <span className="v2-onboarding-summary-label">First Provider</span>
                    <span className="v2-onboarding-summary-value">
                      {form.providerName} ({PROVIDER_TYPES.find(t => t.value === form.providerType)?.label || form.providerType})
                    </span>
                  </div>
                </div>
              )}

              <div className="v2-onboarding-summary-item">
                <div className="v2-onboarding-summary-icon v2-onboarding-summary-icon--text">Em</div>
                <div className="v2-onboarding-summary-info">
                  <span className="v2-onboarding-summary-label">Email Configuration</span>
                  <span className="v2-onboarding-summary-value">
                    Default templates ready. Customize in Settings.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="v2-onboarding-actions">
          <div>
            {step > 0 && (
              <button className="v2-btn v2-btn--outline" onClick={handleBack}>
                Back
              </button>
            )}
          </div>
          <div className="v2-onboarding-actions-right">
            <button className="v2-btn v2-btn--outline" onClick={handleSkip}>
              Skip
            </button>
            {step < STEPS.length - 1 ? (
              <button
                className="v2-btn v2-btn--primary"
                onClick={handleNext}
                disabled={!canContinue()}
              >
                Continue
              </button>
            ) : (
              <button
                className="v2-btn v2-btn--primary"
                onClick={handleComplete}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Go to Dashboard'}
              </button>
            )}
          </div>
        </div>
        {error && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 6, fontSize: 13,
            background: 'rgba(139,58,58,0.08)', color: '#8B3A3A', border: '1px solid #8B3A3A'
          }}>{friendlyErrorMessage(error, errorFallback)}</div>
        )}
      </div>
    </div>
  )
}
